import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheck, faCheckDouble, faReply, faPen, faTrash, faCopy, faShare, faSquareCheck,
    faFilePdf, faFileWord, faFileExcel, faFilePowerpoint,
    faFileZipper, faFileLines, faFile,
    faPhone, faVideo, faArrowDown, faArrowUp,
    faClock, faCircleExclamation, faCircleInfo, faPaperclip, faPlay
} from '@fortawesome/free-solid-svg-icons';

import { SERVER_URL } from '../../utils/api';
import AudioNote from '../AudioNote';
import UploadProgress from '../UploadProgress';
import MediaLightbox from '../MediaLightbox';
import { splitLinks } from '../../utils/linkify';
import LinkPreviewCard from './LinkPreviewCard';
import TaskApprovalCard from './TaskApprovalCard';
import TaskUpdateCard from './TaskUpdateCard';
import AppNotificationCard from './AppNotificationCard';
import {
    MessageMenu, ReactionPicker, ReactionChips, SelectCheck, ForwardedLabel,
    myReaction, copyText, QUICK_REACTIONS
} from './MessageActions';

/**
 * One message in the thread.
 *
 * Renders bubbles the way WhatsApp does — own messages green and right, others
 * white and left, the time and ticks tucked onto the last line of text — so the
 * people moving off WhatsApp do not have to relearn how to read a conversation.
 */

export const fileIcon = (name = '') => {
    const ext = name.split('.').pop().toLowerCase();
    if (ext === 'pdf') return faFilePdf;
    if (['doc', 'docx'].includes(ext)) return faFileWord;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return faFileExcel;
    if (['ppt', 'pptx'].includes(ext)) return faFilePowerpoint;
    if (['zip'].includes(ext)) return faFileZipper;
    if (['txt'].includes(ext)) return faFileLines;
    return faFile;
};

const clockTime = (iso) =>
    new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

/** "2:31" — matches the wording the server stores for the sidebar preview. */
export const callDuration = (seconds) => {
    const total = Math.max(0, Math.round(seconds || 0));
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    if (mins >= 60) {
        return `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${mins}:${String(secs).padStart(2, '0')}`;
};

/**
 * How a finished call reads, from one particular person's side.
 *
 * The server stores one neutral row per call because both people read the same
 * document — so "outgoing" and "incoming" are worked out here, from who placed
 * it. Shared with the Calls tab so a call cannot be described one way in the
 * thread and another way in the log.
 */
export const describeCall = (
    { endReason, durationSec, kind, isGroup, joinedCount, participantCount },
    outgoing
) => {
    const missed = endReason === 'missed' || endReason === 'cancelled';

    /*
     * A group call is described by what happened to the call, not by which
     * direction it went.
     *
     * "You declined" is a true and useful thing to say about a call between two
     * people. Said about a six-person call that ran for twenty minutes without
     * you, it is a strange thing to read in the group's own transcript, where
     * the call is an event the group had rather than an exchange between two
     * particular people. So the group branch reports the call, and leaves
     * direction to the one case where it means something.
     */
    if (isGroup) {
        if (missed) {
            return { label: 'Group call — no answer', missed: !outgoing, detail: '' };
        }
        if (endReason === 'declined') {
            return { label: 'Group call', missed: false, detail: '' };
        }
        const joined = joinedCount || participantCount || 0;
        return {
            label: 'Group call',
            missed: false,
            detail: joined
                ? `${callDuration(durationSec)} · ${joined} joined`
                : callDuration(durationSec)
        };
    }

    if (endReason === 'declined') {
        return {
            label: outgoing ? 'Call declined' : 'You declined',
            missed: false,
            detail: ''
        };
    }
    if (missed) {
        return {
            // The caller did not miss anything; they failed to reach someone.
            label: outgoing ? 'No answer' : `Missed ${kind === 'video' ? 'video call' : 'voice call'}`,
            missed: !outgoing,
            detail: ''
        };
    }
    return {
        label: outgoing ? 'Outgoing' : 'Incoming',
        missed: false,
        detail: callDuration(durationSec)
    };
};

/**
 * Links become real, clickable anchors. Always a new tab, and noopener so the
 * page a colleague linked to can't reach back into this one.
 */
const renderLinks = (text, keyPrefix) => splitLinks(text).map((part, i) => (
    part.href
        ? (
            <a
                key={`${keyPrefix}-${i}`}
                className="msgr-link"
                href={part.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
            >
                {part.text}
            </a>
        )
        : <React.Fragment key={`${keyPrefix}-${i}`}>{part.text}</React.Fragment>
));

/**
 * Draw @mentions as highlighted text, and links as links.
 *
 * Split on the mention pattern rather than injecting HTML: the message body is
 * whatever a colleague typed, and dangerouslySetInnerHTML on it would make
 * every chat box in the company an XSS vector.
 */
const renderText = (text, mentions = []) => {
    if (!text) return null;
    const names = mentions.map((m) => m?.name).filter(Boolean);
    if (!names.length) return renderLinks(text, 't');

    const escaped = names
        .sort((a, b) => b.length - a.length)   // longest first, so "Amit Kumar" wins over "Amit"
        .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

    // A capturing group keeps the delimiters in the split output, so the parts
    // alternate text / mention / text. Whether a part IS a mention is then
    // decided by set membership — NOT by re-running the regex: a /g regex
    // carries lastIndex between .test() calls and would match every other one.
    const parts = text.split(new RegExp(`(@(?:${escaped.join('|')}))`, 'g'));
    const nameSet = new Set(names);

    return parts.map((part, i) => (
        part.startsWith('@') && nameSet.has(part.slice(1))
            ? <span key={i} className="msgr-mention">{part}</span>
            : <React.Fragment key={i}>{renderLinks(part, i)}</React.Fragment>
    ));
};

/*
 * The ticks, WhatsApp's vocabulary: a clock while it is on its way, one tick
 * once the server has it, two once it has reached everyone, blue once everyone
 * has read it. The rule for what counts lives in utils/receipts.js.
 */
const TICK_ICONS = {
    pending: faClock,
    failed: faCircleExclamation,
    sent: faCheck,
    delivered: faCheckDouble,
    read: faCheckDouble
};

const TICK_TITLES = {
    pending: 'Sending…',
    failed: 'Not sent',
    sent: 'Sent',
    delivered: 'Delivered',
    read: 'Read'
};

const MessageBubble = ({
    id,
    message,
    mine,
    myId,
    showAuthor,
    firstOfRun,
    // What my own message's ticks say — see utils/receipts.js. Computed by the
    // thread, which holds the member list, rather than here.
    tick,
    onInfo,
    onRetry,
    onDiscard,
    onCancelUpload,
    onReply,
    onEdit,
    onDelete,
    onJumpTo,
    onCallBack,
    // Opens a task from an approval card: (taskId, canDecide, onDecided).
    onViewTask,
    // WhatsApp-style actions. Each is optional: a surface that passes nothing
    // (the external portal, an oversight read) simply does not offer it.
    onReact,
    onForward,
    onSelect,
    selectMode = false,
    selected = false,
    onToggleSelect,
    // Set only on the one selected message, while it is the only one: reacting
    // is done to a message, not to a pile of them.
    onSelectionReact,
    // Makes the author's name open their profile (the guest portal's info panel).
    onAuthorClick
}) => {
    const [lightbox, setLightbox] = useState(null);
    const hold = useRef({ timer: null, x: 0, y: 0, fired: false });
    // The first message in a thread has no room above it: bring the reaction row
    // into view once, when it appears. A stable callback, so it runs only then.
    const revealOnce = useRef((el) => el?.scrollIntoView?.({ block: 'nearest' })).current;

    /*
     * `id` lands on the root element, not on a wrapper the caller supplies.
     *
     * The thread is a flex column and side is chosen with align-self, which
     * only works on a direct flex child — so wrapping this in a plain <div> to
     * carry the anchor id makes that div the flex child, and every message
     * renders left-aligned and full width regardless of who sent it.
     */

    /*
     * A finished call, WhatsApp-style: an icon, whether it came in or went
     * out, and how long it lasted.
     *
     * Richer than the grey pill below because a call is the one system event
     * people actually scan a thread for — "did I call them back?" — and a line
     * of small grey text reading "Voice call · 2:31" is not scannable. The
     * stored `message.text` says the same thing and remains the sidebar
     * preview and the fallback if the detail is ever missing.
     */
    if (message.systemEvent?.type === 'call' && message.systemEvent.call) {
        const detail = message.systemEvent.call;

        /*
         * `actor` arrives in two shapes and both have to work.
         *
         * The thread endpoint populates it down to { _id, name }, while the
         * socket push from utils/callSignalling.js carries the raw id. Reading
         * only one of them means the direction is right live and wrong after a
         * refresh — which reads as the app forgetting who called whom.
         */
        const actorId = message.systemEvent.actor?._id || message.systemEvent.actor;
        const outgoing = String(actorId || '') === String(myId || '');
        const read = describeCall(detail, outgoing);

        return (
            <div id={id} className={`msgr-msg ${outgoing ? 'out' : 'in'} first-of-run`}>
                <div
                    className={`msgr-bubble msgr-call-bubble${read.missed ? ' is-missed' : ''}${onCallBack ? ' is-clickable' : ''}`}
                    onClick={onCallBack || undefined}
                    title={onCallBack ? 'Call again' : undefined}
                >
                    <span className="msgr-call-icon">
                        <FontAwesomeIcon icon={detail.kind === 'video' ? faVideo : faPhone} />
                    </span>

                    <span className="msgr-call-text">
                        <strong>{read.label}</strong>
                        <span className="msgr-call-meta">
                            <FontAwesomeIcon icon={outgoing ? faArrowUp : faArrowDown} />
                            {clockTime(message.createdAt)}
                            {read.detail && ` · ${read.detail}`}
                        </span>
                    </span>
                </div>
            </div>
        );
    }

    // A self-assigned task waiting on a decision: a card, not a pill.
    if (message.systemEvent?.type === 'task_approval' && message.systemEvent.task?.taskId) {
        return <TaskApprovalCard id={id} message={message} myId={myId} onView={onViewTask} />;
    }

    if (message.systemEvent?.type === 'task_update' && message.systemEvent.task?.taskId) {
        return <TaskUpdateCard id={id} message={message} myId={myId} onView={onViewTask} />;
    }

    if (message.systemEvent?.type === 'app_notification') {
        return <AppNotificationCard id={id} message={message} />;
    }

    // Membership changes render as centred grey pills, not bubbles.
    if (message.systemEvent?.type) {
        return <div id={id} className="msgr-system">{message.text}</div>;
    }

    const attachments = message.attachments || [];
    const visual = attachments.filter((a) => a.type === 'image' || a.type === 'video');
    // Read at render, not subscribed: a bubble is redrawn often enough, and a
    // listener per message for a phone being turned is not worth having.
    const phone = typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 600px)').matches;
    const deleted = Boolean(message.deletedAt);
    const reactions = deleted ? [] : (message.reactions || []);
    // Only the first link gets a card, as on WhatsApp — a message listing
    // five links shouldn't turn into five cards.
    const previewUrl = deleted ? null : (splitLinks(message.text || '').find((p) => p.href)?.href || null);

    const url = (u) => (u?.startsWith('http') ? u : `${SERVER_URL}${u}`);

    /*
     * A message that exists only in this browser — sent, not yet stored, or
     * refused. It has no server id, so nothing that needs one (reply, react,
     * forward, edit, delete, info) can be offered for it yet; the failed state
     * carries its own Retry and Delete instead.
     */
    const local = Boolean(message.local);

    const menuItems = (deleted || local) ? [] : [
        mine && onInfo && { key: 'info', label: 'Info', icon: faCircleInfo, onClick: () => onInfo(message) },
        onReply && { key: 'reply', label: 'Reply', icon: faReply, onClick: () => onReply(message) },
        message.text && { key: 'copy', label: 'Copy', icon: faCopy, onClick: () => copyText(message.text) },
        onForward && { key: 'forward', label: 'Forward', icon: faShare, onClick: () => onForward(message) },
        onSelect && { key: 'select', label: 'Select', icon: faSquareCheck, onClick: () => onSelect(message) },
        mine && onEdit && { key: 'edit', label: 'Edit', icon: faPen, onClick: () => onEdit(message) },
        onDelete && { key: 'delete', label: 'Delete', icon: faTrash, danger: true, onClick: () => onDelete(message) }
    ];

    const picker = !deleted && !selectMode && onReact && (
        <ReactionPicker
            myEmoji={myReaction(reactions, myId)}
            onPick={(emoji) => onReact(message, emoji)}
            align={mine ? 'right' : 'left'}
        />
    );

    /*
     * Touch and hold to select, as in the app and on WhatsApp.
     *
     * Touch only: a mouse already has the ⋮ menu and a hover smiley, and a
     * held mouse button is how people start selecting text to copy. The click
     * and context menu that follow a hold are swallowed, so letting go does
     * not also open the image or play the note that was under the finger.
     */
    const HOLD_MS = 450;
    const canHold = !selectMode && !deleted && !local && Boolean(onSelect);
    const cancelHold = () => {
        clearTimeout(hold.current.timer);
        hold.current.timer = null;
    };
    const fireHold = () => {
        cancelHold();
        hold.current.fired = true;
        navigator.vibrate?.(15);
        onSelect(message);
    };
    const holdHandlers = {
        onPointerDown: (e) => {
            hold.current.fired = false;
            if (!canHold || e.pointerType === 'mouse') return;
            hold.current.x = e.clientX;
            hold.current.y = e.clientY;
            cancelHold();
            hold.current.timer = setTimeout(fireHold, HOLD_MS);
        },
        // A scroll is not a hold.
        onPointerMove: (e) => {
            if (hold.current.timer && Math.hypot(e.clientX - hold.current.x, e.clientY - hold.current.y) > 10) cancelHold();
        },
        onPointerUp: () => {
            cancelHold();
            // Not every browser sends a click after a hold; one that does sends
            // it straight after this. Remember when, so only that click is eaten.
            if (hold.current.fired) hold.current.releasedAt = Date.now();
        },
        onPointerCancel: cancelHold,
        // Android raises the context menu for a held touch, sometimes before the
        // timer: take it as the hold itself.
        onContextMenu: (e) => {
            if (hold.current.timer) { e.preventDefault(); fireHold(); return; }
            if (hold.current.fired) e.preventDefault();
        }
    };

    /*
     * While selecting, a click anywhere on the row ticks it and does nothing
     * else. Captured before it reaches the children, so it cannot also open an
     * image, play a voice note or follow a file link. The click that ends the
     * hold which started selecting is swallowed instead — otherwise it would
     * untick the message it just selected.
     */
    const onClickCapture = (e) => {
        if (hold.current.fired && Date.now() - (hold.current.releasedAt || 0) < 600) {
            hold.current.fired = false;
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        if (!selectMode) return;
        // The reaction row on a selected message is the one thing in it that
        // is not "tick this".
        if (e.target.closest?.('.mx-select-react')) return;
        e.preventDefault();
        e.stopPropagation();
        if (!deleted && !local) onToggleSelect?.(message);
    };

    const rowClass = [
        'msgr-msg',
        'mx-hover-host',
        mine ? 'out' : 'in',
        firstOfRun ? 'first-of-run' : '',
        reactions.length ? 'has-reactions' : '',
        selectMode ? 'is-selectable' : '',
        selected ? 'is-selected' : ''
    ].filter(Boolean).join(' ');

    return (
        <div
            id={id}
            className={rowClass}
            onClickCapture={onClickCapture}
            {...holdHandlers}
        >
            {selectMode && !deleted && !local && <SelectCheck checked={selected} />}

            {onSelectionReact && (
                <div ref={revealOnce} className={`mx-react-bar mx-select-react ${mine ? 'is-mine-side' : ''}`} role="menu">
                    {QUICK_REACTIONS.map((emoji) => {
                        const active = myReaction(reactions, myId) === emoji;
                        return (
                            <button
                                key={emoji}
                                type="button"
                                className={active ? 'is-mine' : ''}
                                onClick={() => onSelectionReact(message, emoji)}
                                title={active ? 'Remove reaction' : 'React'}
                            >
                                {emoji}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* The smiley sits on the inner side of the bubble, as on WhatsApp. */}
            {mine && picker}

            <div className={`msgr-bubble ${message.externalSender ? 'from-external' : ''}`}>
                {!selectMode && <MessageMenu items={menuItems} className="msgr-bubble-dots" />}

                {/*
                  * The External badge (feature draft F2.4).
                  *
                  * Shown on every message an outsider wrote, and shown even when
                  * the author line is otherwise suppressed for a run of
                  * consecutive messages - the point of the badge is that nobody
                  * can reply to a vendor believing they are talking to a
                  * colleague, and a badge that appears only on the first message
                  * of a run is a badge that is missing exactly when somebody has
                  * scrolled into the middle of one.
                  */}
                {!mine && message.externalSender && (
                    <div className="msgr-bubble-author">
                        {onAuthorClick
                            ? <button type="button" className="msgr-author-link" onClick={() => onAuthorClick(message)}>{message.externalSender.name || 'External participant'}</button>
                            : (message.externalSender.name || 'External participant')}
                        <span className="ext-badge">Guest</span>
                    </div>
                )}

                {!mine && showAuthor && !message.externalSender && (
                    <div className="msgr-bubble-author">
                        {onAuthorClick && message.sender
                            ? <button type="button" className="msgr-author-link" onClick={() => onAuthorClick(message)}>{message.sender.name || 'Unknown'}</button>
                            : (message.sender?.name || 'Unknown')}
                    </div>
                )}

                {message.forwarded && !deleted && <ForwardedLabel />}

                {message.replyTo?.messageId && (
                    <div
                        className="msgr-quote"
                        onClick={() => onJumpTo?.(message.replyTo.messageId)}
                        title="Go to the original message"
                    >
                        <strong>{message.replyTo.senderName || 'Message'}</strong>
                        <span>{message.replyTo.text || 'Attachment'}</span>
                    </div>
                )}

                {/*
                  * Still uploading: the files have no URL to show yet, so they
                  * are named instead. The real previews replace these the moment
                  * the server has them.
                  */}
                {local && attachments.length > 0 && (
                    <div className="msgr-attachments">
                        {attachments.map((a, i) => (
                            <div key={i} className="msgr-file is-uploading">
                                <FontAwesomeIcon icon={faPaperclip} className="msgr-file-icon" />
                                <div style={{ minWidth: 0 }}>
                                    <div className="msgr-file-name">{a.fileName || 'Attachment'}</div>
                                    <div className="msgr-file-kind">
                                        {message.failed ? 'Not sent'
                                            : message.uploadState === 'queued' ? 'Waiting to upload'
                                                : message.uploadState === 'cancelling' ? 'Cancelling…'
                                                    : message.uploadState === 'processing' ? 'Saving…' : 'Uploading…'}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {local && message.pending && attachments.length > 0 && (
                    <UploadProgress
                        progress={message.uploadProgress}
                        count={attachments.length}
                        queued={message.uploadState === 'queued'}
                        cancelling={message.uploadState === 'cancelling'}
                        onCancel={onCancelUpload ? () => onCancelUpload(message) : undefined}
                    />
                )}

                {!deleted && !local && attachments.length > 0 && (
                    <div className="msgr-attachments">
                        {attachments.map((a, i) => {
                            if (a.type === 'image') {
                                return (
                                    <img
                                        key={a._id || i}
                                        src={url(a.url)}
                                        alt={a.fileName || 'photo'}
                                        className="msgr-media-img"
                                        loading="lazy"
                                        onClick={() => setLightbox(visual.findIndex((v) => v._id === a._id))}
                                    />
                                );
                            }

                            if (a.type === 'video') {
                                return (
                                    <div key={a._id || i} className="msgr-media-video-wrap">
                                        {/* On a phone the video opens in the viewer, like a photo
                                            does — that is where it can be swiped away or turned
                                            sideways. Playing it in place stays the desktop way. */}
                                        <video
                                            src={phone ? `${url(a.url)}#t=0.1` : url(a.url)}
                                            className="msgr-media-video"
                                            controls={!phone}
                                            playsInline
                                            preload="metadata"
                                        />
                                        {phone && (
                                            <button
                                                type="button"
                                                className="msgr-media-play"
                                                aria-label={`Play ${a.fileName || 'video'}`}
                                                onClick={() => setLightbox(visual.findIndex((v) => v._id === a._id))}
                                            >
                                                <span><FontAwesomeIcon icon={faPlay} /></span>
                                            </button>
                                        )}
                                        {/* Only until there is a copy that actually plays, which
                                            is a minute or so — not until the night job moves the
                                            file to S3, which is nobody's business but the server's
                                            and used to leave this reading "Optimising…" all day
                                            over a video that was fine hours earlier. The raw
                                            upload is still offered meanwhile, because a WebM
                                            screen recording does play in Chrome; it just cannot be
                                            seeked, and Safari will not open it at all. */}
                                        {a.status === 'processing_compression' && !a.playable && (
                                            <span className="msgr-media-processing">Preparing…</span>
                                        )}
                                    </div>
                                );
                            }

                            if (a.type === 'audio') {
                                return <AudioNote key={a._id || i} media={a} mine={mine}
                                    messageId={message._id} senderName={mine ? 'You' : message.sender?.name} />;
                            }

                            return (
                                <a
                                    key={a._id || i}
                                    className="msgr-file"
                                    href={url(a.url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download={a.fileName}
                                >
                                    <FontAwesomeIcon icon={fileIcon(a.fileName)} className="msgr-file-icon" />
                                    <div style={{ minWidth: 0 }}>
                                        <div className="msgr-file-name">{a.fileName || 'File'}</div>
                                        <div className="msgr-file-kind">
                                            {(a.fileName || '').split('.').pop()}
                                        </div>
                                    </div>
                                </a>
                            );
                        })}
                    </div>
                )}

                {/* WhatsApp's card for the first link in the message, above the text. */}
                {previewUrl && <LinkPreviewCard url={previewUrl} />}

                {(message.text || deleted) && (
                    <div className={`msgr-bubble-text ${deleted ? 'deleted' : ''}`}>
                        {deleted
                            ? 'This message was deleted'
                            : renderText(message.text, message.mentions)}
                    </div>
                )}

                <div className="msgr-meta">
                    {message.editedAt && !deleted && <span>edited</span>}
                    <span>{clockTime(message.createdAt)}</span>
                    {mine && !deleted && tick && (
                        <FontAwesomeIcon
                            className={`msgr-ticks is-${tick}${tick === 'read' ? ' read' : ''}`}
                            icon={TICK_ICONS[tick] || faCheck}
                            title={TICK_TITLES[tick]}
                        />
                    )}
                </div>
                <div style={{ clear: 'both' }} />

                {message.failed && (
                    <div className="msgr-failed" role="alert">
                        <span>{message.error || 'Could not send.'}</span>
                        {onRetry && <button type="button" onClick={() => onRetry(message)}>Retry</button>}
                        {onDiscard && <button type="button" onClick={() => onDiscard(message)}>Delete</button>}
                    </div>
                )}

                <ReactionChips
                    reactions={reactions}
                    myId={myId}
                    onToggle={onReact && !selectMode ? (emoji) => onReact(message, emoji) : null}
                    className="msgr-reactions"
                />
            </div>

            {!mine && picker}

            {lightbox !== null && lightbox >= 0 && (
                <MediaLightbox
                    items={visual.map((v) => ({ ...v, url: url(v.url) }))}
                    index={lightbox}
                    onClose={() => setLightbox(null)}
                    onIndexChange={setLightbox}
                />
            )}
        </div>
    );
};

export default MessageBubble;
